import React, { useState, useEffect, useMemo } from "react";
import Layout from "./Layout";
import Calendar from "react-calendar";
import 'react-calendar/dist/Calendar.css';
import { Calendar as CalendarIcon, Clock, User, Filter, Plus, Edit2, Trash2, Download, Search } from "lucide-react";

const AppointmentsPage = ({ user, onLogout }) => {
  const [appointments, setAppointments] = useState([]);
  const [clients, setClients] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'calendar'
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filters, setFilters] = useState({
    status: '',
    client_id: '',
    executive_id: '',
    hotel_id: '',
    start_date: '',
    end_date: '',
    search: ''
  });

  // Separate state for debounced search
  const [searchInput, setSearchInput] = useState('');

  // Executive report download states (only for executive role)
  const [downloading, setDownloading] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    date: '',
    time: '',
    status: 'Scheduled',
    purpose: '',
    conclusion: ''
  });

  const statusOptions = ["Scheduled", "In Progress", "Completed", "Cancelled", "Rescheduled"];
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

  // Fetch data on component mount
  useEffect(() => {
    fetchAppointments();
    fetchClients();
    fetchHotels();
    if (user?.role === "admin" || user?.role === "manager") {
      fetchExecutives();
    }
  }, []); // Solo ejecutar una vez al montar el componente

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Separate useEffect for non-search filters only
  useEffect(() => {
    if (filters.status || filters.start_date || filters.end_date) {
      fetchAppointments();
    }
  }, [filters.status, filters.start_date, filters.end_date]); // Solo refetch para filtros del servidor

  const fetchAppointments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      
      if (filters.status) params.append('status', filters.status);
      if (filters.client_id) params.append('client_id', filters.client_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);

      const response = await fetch(`${backendUrl}/api/appointments?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      } else {
        console.error('Failed to fetch appointments');
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.hotel_id) params.append('hotel_id', filters.hotel_id);
      
      const response = await fetch(`${backendUrl}/api/clients?${params}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setClients(data);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchHotels = async () => {
    try {
      const response = await fetch(`${backendUrl}/api/hotels`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setHotels(data);
      }
    } catch (error) {
      console.error('Error fetching hotels:', error);
    }
  };

  const fetchExecutives = async () => {
    try {
      console.log('[AppointmentsPage] Fetching executives for user:', user?.username, user?.role);
      const response = await fetch(`${backendUrl}/api/executives`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('[AppointmentsPage] Executives fetched successfully:', data.length, 'executives');
        console.log('[AppointmentsPage] Executive names:', data.map(e => e.name));
        setExecutives(data);
      } else {
        console.error('[AppointmentsPage] Failed to fetch executives - Status:', response.status);
      }
    } catch (error) {
      console.error('[AppointmentsPage] Error fetching executives:', error);
    }
  };

  // Executive visits download function (only for executive role)
  const handleDownloadExecutiveReport = async (format) => {
    if (user?.role !== "executive") return;
    
    setDownloading(true);
    try {
      const formData = new FormData();
      formData.append("format", format);
      if (filters.status) formData.append("status", filters.status);
      if (filters.start_date) formData.append("start_date", filters.start_date);
      if (filters.end_date) formData.append("end_date", filters.end_date);

      const response = await fetch(`${backendUrl}/api/executive/visits/export`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        // Create download link
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Extract filename from Content-Disposition header or create default
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `mis_visitas_${new Date().toISOString().split('T')[0]}.${format}`;
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        // Success notification using alert (since we might not have toast here)
        alert("Reporte descargado exitosamente");
      } else {
        const errorData = await response.json();
        alert(`Error: ${errorData.detail || 'Error al descargar el reporte'}`);
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      alert("Error al descargar el reporte");
    } finally {
      setDownloading(false);
    }
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    
    try {
      console.log('Creating appointment with data:', formData);
      
      const response = await fetch(`${backendUrl}/api/appointments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setShowCreateModal(false);
        setFormData({
          client_id: '',
          date: '',
          time: '',
          status: 'Scheduled',
          purpose: '',
          conclusion: ''
        });
        fetchAppointments();
        alert('Appointment created successfully!');
      } else {
        const errorData = await response.text();
        console.error('Server error:', errorData);
        
        try {
          const errorJson = JSON.parse(errorData);
          if (errorJson.detail) {
            if (Array.isArray(errorJson.detail)) {
              // Handle Pydantic validation errors
              const errorMessages = errorJson.detail.map(err => {
                const location = err.loc ? err.loc.join('.') : 'field';
                const message = err.msg || 'Invalid value';
                return `${location}: ${message}`;
              }).join(', ');
              alert(`Validation error: ${errorMessages}`);
            } else if (typeof errorJson.detail === 'object') {
              // Handle complex error objects
              alert(`Error: ${JSON.stringify(errorJson.detail, null, 2)}`);
            } else {
              alert(`Error: ${errorJson.detail}`);
            }
          } else {
            alert('Failed to create appointment');
          }
        } catch (parseError) {
          console.error('Parse error:', parseError);
          alert(`Server error: ${errorData}`);
        }
      }
    } catch (error) {
      console.error('Error creating appointment:', error);
      alert(`Network error: ${error.message}`);
    }
  };

  const handleEditAppointment = async (e) => {
    e.preventDefault();
    
    console.log('Form submission started', { selectedAppointment, formData });
    
    if (!selectedAppointment || !selectedAppointment.id) {
      alert('Error: No appointment selected for editing');
      return;
    }
    
    try {
      console.log('Updating appointment with data:', formData);
      
      // Only include fields that should be updated
      const updateData = {};
      
      // Always include status as it's the most commonly updated field
      if (formData.status) {
        updateData.status = formData.status;
      }
      
      // Only include date if it's provided and different from original
      if (formData.date && formData.date !== selectedAppointment.date) {
        updateData.date = formData.date;
      }
      
      // Only include time if it's provided and different from original  
      if (formData.time && formData.time !== selectedAppointment.time) {
        updateData.time = formData.time;
      }
      
      // Include purpose and conclusion (can be empty strings)
      updateData.purpose = formData.purpose || '';
      updateData.conclusion = formData.conclusion || '';
      
      console.log('Sending update data:', updateData);
      
      const response = await fetch(`${backendUrl}/api/appointments/${selectedAppointment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        setShowEditModal(false);
        setSelectedAppointment(null);
        setFormData({
          client_id: '',
          date: '',
          time: '',
          status: 'Scheduled',
          purpose: '',
          conclusion: ''
        });
        fetchAppointments();
        alert('Appointment updated successfully!');
      } else {
        const errorData = await response.text();
        console.error('Server error:', errorData);
        
        try {
          const errorJson = JSON.parse(errorData);
          if (errorJson.detail) {
            if (Array.isArray(errorJson.detail)) {
              // Handle Pydantic validation errors
              const errorMessages = errorJson.detail.map(err => {
                const location = err.loc ? err.loc.join('.') : 'field';
                const message = err.msg || 'Invalid value';
                return `${location}: ${message}`;
              }).join(', ');
              alert(`Validation error: ${errorMessages}`);
            } else if (typeof errorJson.detail === 'object') {
              // Handle complex error objects
              alert(`Error: ${JSON.stringify(errorJson.detail, null, 2)}`);
            } else {
              alert(`Error: ${errorJson.detail}`);
            }
          } else {
            alert('Failed to update appointment');
          }
        } catch (parseError) {
          console.error('Parse error:', parseError);
          alert(`Server error: ${errorData}`);
        }
      }
    } catch (error) {
      console.error('Error updating appointment:', error);
      alert(`Network error: ${error.message}`);
    }
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) {
      return;
    }

    try {
      const response = await fetch(`${backendUrl}/api/appointments/${appointmentId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        fetchAppointments();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete appointment');
      }
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert('Failed to delete appointment');
    }
  };

  const handleExport = async (format) => {
    try {
      console.log('Starting export with format:', format);
      
      const params = new URLSearchParams();
      
      if (filters.status) params.append('status', filters.status);
      if (filters.executive_id) params.append('executive_id', filters.executive_id);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      params.append('format', format);

      console.log('Export params:', params.toString());

      const response = await fetch(`${backendUrl}/api/appointments/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        credentials: 'include',
        body: params
      });

      console.log('Export response status:', response.status);
      console.log('Export response headers:', response.headers);

      if (response.ok) {
        const blob = await response.blob();
        console.log('Blob size:', blob.size, 'bytes');
        
        if (blob.size === 0) {
          alert('Export failed: Empty file received');
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `appointments_export_${new Date().toISOString().slice(0,10)}.${format}`;
        
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        
        console.log('Download filename:', filename);
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Show success message
        alert(`Export successful: ${filename}`);
      } else {
        const errorText = await response.text();
        console.error('Export error response:', errorText);
        alert(`Export failed: ${response.status} - ${errorText}`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert(`Export failed: ${error.message}`);
    }
  };

  const openEditModal = (appointment) => {
    setSelectedAppointment(appointment);
    setFormData({
      client_id: appointment.client_id,
      date: appointment.date,
      time: appointment.time,
      status: appointment.status,
      purpose: appointment.purpose || '',
      conclusion: appointment.conclusion || ''
    });
    setShowEditModal(true);
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.client_name : 'Unknown Client';
  };

  const getHotelName = (hotelId) => {
    const hotel = hotels.find(h => h.id === hotelId);
    return hotel ? hotel.hotel_name : 'Unknown Hotel';
  };

  const getExecutiveName = (executiveId) => {
    console.log('[AppointmentsPage] getExecutiveName called with ID:', executiveId);
    console.log('[AppointmentsPage] Available executives:', executives.length, executives.map(e => ({id: e.id, name: e.name})));
    
    const executive = executives.find(e => e.id === executiveId);
    const result = executive ? executive.name : 'Unknown Executive';
    
    console.log('[AppointmentsPage] Executive found:', executive ? 'YES' : 'NO', '- Result:', result);
    return result;
  };

  const getStatusColor = (status) => {
    const colors = {
      'Scheduled': 'bg-blue-100 text-blue-800',
      'In Progress': 'bg-yellow-100 text-yellow-800',
      'Completed': 'bg-green-100 text-green-800',
      'Cancelled': 'bg-red-100 text-red-800',
      'Rescheduled': 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  // Calendar helper functions
  const getAppointmentsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return appointments.filter(appointment => appointment.date === dateStr);
  };

  const getTileContent = ({ date, view }) => {
    if (view === 'month') {
      const dayAppointments = getAppointmentsForDate(date);
      if (dayAppointments.length > 0) {
        return (
          <div className="flex justify-center mt-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          </div>
        );
      }
    }
    return null;
  };

  const getTileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dayAppointments = getAppointmentsForDate(date);
      if (dayAppointments.length > 0) {
        return 'has-appointments';
      }
    }
    return null;
  };

  // Filter appointments for list view with useMemo for performance
  const filteredAppointments = useMemo(() => {
    return appointments.filter(appointment => {
      // Search filter
      if (filters.search) {
        const clientName = getClientName(appointment.client_id).toLowerCase();
        const executiveName = getExecutiveName(appointment.executive_id).toLowerCase();
        const searchTerm = filters.search.toLowerCase();
        
        const matchesSearch = clientName.includes(searchTerm) || 
               executiveName.includes(searchTerm) ||
               appointment.status.toLowerCase().includes(searchTerm);
        
        if (!matchesSearch) return false;
      }

      // Executive filter (for admin view)
      if (filters.executive_id && appointment.executive_id !== filters.executive_id) {
        return false;
      }

      // Hotel filter (filter by client's hotel)
      if (filters.hotel_id) {
        const client = clients.find(c => c.id === appointment.client_id);
        if (!client || client.hotel_id !== filters.hotel_id) {
          return false;
        }
      }

      return true;
    });
  }, [appointments, filters.search, filters.executive_id, filters.hotel_id, clients]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg">Loading appointments...</div>
      </div>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="appointments-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between">
          <h1 className="page-title">Appointments</h1>
          <div className="flex items-center gap-4">
            {/* View Toggle */}
            <div className="btn-group">
              <button 
                className={`btn ${view === 'list' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setView('list')}
              >
                List View
              </button>
              <button 
                className={`btn ${view === 'calendar' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setView('calendar')}
              >
                Calendar View
              </button>
            </div>
            
            {/* Create Appointment Button - Only Admin and Executive */}
            {(user?.role === "admin" || user?.role === "executive") && (
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus size={16} />
                New Appointment
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4 mb-4">
          <div className="form-group">
            <label>Search</label>
            <div className="input-with-icon">
              <Search size={16} />
              <input
                type="text"
                placeholder="Search appointments..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="form-input"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
              className="form-input"
            >
              <option value="">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          {user?.role === "admin" && (
            <div className="form-group">
              <label>Hotel</label>
              <select
                value={filters.hotel_id}
                onChange={(e) => setFilters({...filters, hotel_id: e.target.value})}
                className="form-input"
              >
                <option value="">All Hotels</option>
                {hotels.map(hotel => (
                  <option key={hotel.id} value={hotel.id}>{hotel.hotel_name}</option>
                ))}
              </select>
            </div>
          )}

          {user?.role === "admin" && (
            <div className="form-group">
              <label>Executive</label>
              <select
                value={filters.executive_id}
                onChange={(e) => setFilters({...filters, executive_id: e.target.value})}
                className="form-input"
              >
                <option value="">All Executives</option>
                {executives.map(executive => (
                  <option key={executive.id} value={executive.id}>{executive.name}</option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({...filters, start_date: e.target.value})}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({...filters, end_date: e.target.value})}
              className="form-input"
            />
          </div>
        </div>

        {/* Export Section - Admin and Manager */}
        {(user?.role === "admin" || user?.role === "manager") && (
          <div className="export-section bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-800">
                {user?.role === "admin" ? "Export Visit Reports" : "Descargar Reportes del Equipo"}
              </h3>
              <div className="flex gap-3">
                <select 
                  id="exportFormat"
                  className="form-input form-input-sm min-w-24"
                  defaultValue=""
                >
                  <option value="">Select Format</option>
                  <option value="xlsx">Excel (.xlsx)</option>
                  <option value="csv">CSV (.csv)</option>
                </select>
                <button 
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const format = document.getElementById('exportFormat').value;
                    if (format) {
                      handleExport(format);
                    } else {
                      alert('Please select an export format');
                    }
                  }}
                  title="Export appointments"
                >
                  <Download size={14} />
                  Export
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Executive Report Download Section - Only for Executive */}
        {user?.role === "executive" && (
          <div className="export-section bg-blue-50 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-blue-800">
                <Download size={16} className="inline mr-2" />
                Descargar Mis Reportes de Visitas
              </h3>
              <div className="flex gap-2">
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={() => handleDownloadExecutiveReport('xlsx')}
                  disabled={downloading}
                  title="Descargar reporte en Excel"
                >
                  {downloading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <Download size={14} />
                  )}
                  Excel
                </button>
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={() => handleDownloadExecutiveReport('csv')}
                  disabled={downloading}
                  title="Descargar reporte en CSV"
                >
                  {downloading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <Download size={14} />
                  )}
                  CSV
                </button>
                <button 
                  className="btn btn-outline btn-sm"
                  onClick={() => handleDownloadExecutiveReport('pdf')}
                  disabled={downloading}
                  title="Descargar reporte en PDF"
                >
                  {downloading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <Download size={14} />
                  )}
                  PDF
                </button>
              </div>
            </div>
            <p className="text-xs text-blue-600 mt-2">
              Los reportes incluyen solo tus citas programadas. Usa los filtros arriba para personalizar el rango de fechas o estado.
            </p>
          </div>
        )}
      </div>

      {/* Content based on view */}
      {view === 'list' ? (
        <div className="appointments-table">
          <div className="w-full overflow-x-auto">
            <table className="data-table w-full">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Client</th>
                  {(user?.role === "admin" || user?.role === "manager") && <th>Executive</th>}
                  <th>Status</th>
                  <th>Purpose</th>
                  <th>Conclusion</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredAppointments.length === 0 ? (
                  <tr>
                    <td colSpan={(user?.role === "admin" || user?.role === "manager") ? "8" : "7"} className="text-center py-8">
                      No appointments found
                    </td>
                  </tr>
                ) : (
                  filteredAppointments.map(appointment => (
                    <tr key={appointment.id}>
                      <td>{appointment.date}</td>
                      <td>{appointment.time}</td>
                      <td>{getClientName(appointment.client_id)}</td>
                      {(user?.role === "admin" || user?.role === "manager") && (
                        <td>{getExecutiveName(appointment.executive_id)}</td>
                      )}
                      <td>
                        <span className={`status-badge ${getStatusColor(appointment.status)}`}>
                          {appointment.status}
                        </span>
                      </td>
                      <td className="max-w-32 truncate" title={appointment.purpose || ''}>
                        {appointment.purpose || '-'}
                      </td>
                      <td className="max-w-32 truncate" title={appointment.conclusion || ''}>
                        {appointment.conclusion || '-'}
                      </td>
                      <td>
                        <div className="flex gap-2">
                          {/* Solo executives y admins pueden editar sus propias citas */}
                          {(user?.role === "admin" || 
                            (user?.role === "executive" && appointment.executive_id === user.id)) && (
                            <button
                              onClick={() => openEditModal(appointment)}
                              className="btn-icon btn-sm"
                              title="Edit appointment"
                            >
                              <Edit2 size={14} />
                            </button>
                          )}
                          {/* Solo admins pueden borrar citas */}
                          {user?.role === "admin" && (
                            <button
                              onClick={() => handleDeleteAppointment(appointment.id)}
                              className="btn-icon btn-sm text-red-600 hover:bg-red-50"
                              title="Delete appointment"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="calendar-view">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendar */}
            <div className="lg:col-span-2">
              <div className="bg-white p-4 rounded-lg border">
                <Calendar
                  onChange={setSelectedDate}
                  value={selectedDate}
                  tileContent={getTileContent}
                  tileClassName={getTileClassName}
                  className="w-full"
                />
              </div>
            </div>

            {/* Selected Date Appointments */}
            <div className="lg:col-span-1">
              <div className="bg-white p-4 rounded-lg border">
                <h3 className="text-lg font-semibold mb-4">
                  Appointments for {selectedDate.toLocaleDateString()}
                </h3>
                
                {getAppointmentsForDate(selectedDate).length === 0 ? (
                  <p className="text-gray-500 text-center py-8">
                    No appointments scheduled for this date
                  </p>
                ) : (
                  <div className="space-y-3">
                    {getAppointmentsForDate(selectedDate).map(appointment => (
                      <div key={appointment.id} className="p-3 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Clock size={16} />
                            <span className="font-medium">{appointment.time}</span>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(appointment.status)}`}>
                            {appointment.status}
                          </span>
                        </div>
                        <div className="mt-2">
                          <p className="text-sm font-medium">{getClientName(appointment.client_id)}</p>
                          {user?.role === "admin" && (
                            <p className="text-xs text-gray-500">{getExecutiveName(appointment.executive_id)}</p>
                          )}
                        </div>
                        <div className="mt-2 flex gap-2">
                          {/* Solo executives y admins pueden editar sus propias citas */}
                          {(user?.role === "admin" || 
                            (user?.role === "executive" && appointment.executive_id === user.id)) && (
                            <button
                              onClick={() => openEditModal(appointment)}
                              className="btn-icon btn-sm"
                              title="Edit appointment"
                            >
                              <Edit2 size={12} />
                            </button>
                          )}
                          {/* Solo admins pueden borrar citas */}
                          {user?.role === "admin" && (
                            <button
                              onClick={() => handleDeleteAppointment(appointment.id)}
                              className="btn-icon btn-sm text-red-600 hover:bg-red-50"
                              title="Delete appointment"
                            >
                              <Trash2 size={12} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Appointment Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Create New Appointment</h2>
              <button 
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateAppointment} className="modal-body">
              <div className="form-group">
                <label>Client *</label>
                <select
                  value={formData.client_id}
                  onChange={(e) => setFormData({...formData, client_id: e.target.value})}
                  className="form-input"
                  required
                >
                  <option value="">Select a client</option>
                  {clients.map(client => (
                    <option key={client.id} value={client.id}>
                      {client.client_name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="form-input"
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Motivo de la Cita</label>
                <textarea
                  value={formData.purpose}
                  onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                  className="form-input"
                  placeholder="Describe el motivo de la cita..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Conclusión de la Cita</label>
                <textarea
                  value={formData.conclusion}
                  onChange={(e) => setFormData({...formData, conclusion: e.target.value})}
                  className="form-input"
                  placeholder="Conclusión y notas de la cita..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Appointment Modal */}
      {showEditModal && selectedAppointment && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Edit Appointment</h2>
              <button 
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditAppointment} className="modal-body">
              <div className="form-group">
                <label>Client</label>
                <input
                  type="text"
                  value={getClientName(selectedAppointment.client_id)}
                  disabled
                  className="form-input disabled"
                />
                <small className="text-gray-500">Client cannot be changed after creation</small>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({...formData, date: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={formData.time}
                    onChange={(e) => setFormData({...formData, time: e.target.value})}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({...formData, status: e.target.value})}
                  className="form-input"
                >
                  {statusOptions.map(status => (
                    <option key={status} value={status}>{status}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Motivo de la Cita</label>
                <textarea
                  value={formData.purpose}
                  onChange={(e) => setFormData({...formData, purpose: e.target.value})}
                  className="form-input"
                  placeholder="Describe el motivo de la cita..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label>Conclusión de la Cita</label>
                <textarea
                  value={formData.conclusion}
                  onChange={(e) => setFormData({...formData, conclusion: e.target.value})}
                  className="form-input"
                  placeholder="Conclusión y notas de la cita..."
                  rows="3"
                />
              </div>

              <div className="modal-actions">
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Update Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </Layout>
  );
};

export default AppointmentsPage;